import React from "react";
import Select from "react-select";
import { useResponsive } from "../../../hooks/useResponsive";
import tools from "../../../utils/tools";

interface ResponsiveServicesSectionProps {
  form: any;
  setForm: (form: any) => void;
  serviceReducer: any;
  isColored: boolean;
  colors: any[];
}

const ServicesSection: React.FC<ResponsiveServicesSectionProps> = ({
  form,
  setForm,
  serviceReducer,
  isColored,
  colors,
}) => {
  const { isMobile, isTablet } = useResponsive();
  // Responsive styles for react-select
  const getSelectStyles = () => ({
    control: (provided: any) => ({
      ...provided,
      minHeight: isMobile ? "44px" : "40px",
      fontSize: isMobile ? "16px" : "14px",
      border: "1px solid #E7E6E5",
      borderRadius: "4px",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: isMobile ? "8px 12px" : "4px 8px",
      maxHeight: isMobile ? "120px" : "80px",
      overflow: "auto",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: "#c89dc7",
      color: "white",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "white",
      fontSize: isMobile ? "12px" : "11px",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: "white",
      ":hover": {
        backgroundColor: "#b388b8",
        color: "white",
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#c89dc7"
        : state.isFocused
          ? "#f8f9fa"
          : "white",
      color: state.isSelected ? "white" : "#333",
      fontSize: isMobile ? "14px" : "13px",
      padding: isMobile ? "12px 16px" : "8px 12px",
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      maxHeight: isMobile ? "200px" : "300px",
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: isMobile ? "180px" : "280px",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  });

  const handleServiceChange = (selected: any) => {
    setForm((prev: any) => ({
      ...prev,
      serviceId: selected.value,
    }));
  };

  const handleAddService = () => {
    if (!form?.serviceId) {
      alert("لطفاً ابتدا یک خدمت انتخاب کنید");
      return;
    }

    // Check if service already exists
    const existingService = form.orderServices.find(
      (s: any) => s.serviceId === form.serviceId,
    );
    if (existingService) {
      alert("این خدمت قبلاً اضافه شده است");
      return;
    }

    setForm((prev: any) => ({
      ...prev,
      orderServices: [
        ...prev.orderServices,
        {
          serviceId: prev.serviceId,
          count: 1,
          colors: [],
        },
      ],
    }));
  };

  const handleRemoveService = (serviceId: number) => {
    if (confirm("آیا از حذف این خدمت مطمئن هستید؟")) {
      setForm((prev: any) => ({
        ...prev,
        orderServices: prev.orderServices.filter(
          (e: any) => e.serviceId !== serviceId,
        ),
      }));
    }
  };

  const handleServiceItemChange = (
    serviceId: number,
    field: string,
    value: any,
  ) => {
    setForm((prev: any) => {
      const index = prev.orderServices.findIndex(
        (e: any) => e.serviceId === serviceId,
      );
      if (index === -1) return prev;

      const updatedServices = [...prev.orderServices];
      updatedServices[index] = {
        ...updatedServices[index],
        [field]: value,
      };

      return {
        ...prev,
        orderServices: updatedServices,
      };
    });
  };

  const renderServiceRows = () => {
    return form?.orderServices?.map((orderProduct: any, index: number) => {
      const service = serviceReducer.allServices?.find(
        (e: any) => e.id === orderProduct.serviceId,
      );
      const singlePrice = orderProduct.id
        ? orderProduct.singlePrice
        : service?.price * (form?.isUrgent ? 1.5 : 1);
      const price = orderProduct.count * singlePrice;

      if (isMobile) {
        // Mobile card layout
        return (
          <div key={`service-card-${index}`} className="service-card-mobile">
            <div className="service-card-header">
              <div className="service-name">
                <Select
                  className="service-select-mobile"
                  styles={getSelectStyles()}
                  options={serviceReducer.allServices
                    .filter((e: any) => e.price > 0)
                    .map((e: any) => ({
                      value: e.id,
                      label: tools
                        .findAncestors(serviceReducer.allServices, e.id)
                        ?.filter((e: any, index: number) => index < 3)
                        .reverse()
                        ?.reduce(
                          (acc: string, curr: any, index: number) =>
                            acc + ((index === 0 ? "" : " > ") + curr?.title),
                          "",
                        ),
                    }))}
                  value={{
                    value: orderProduct.serviceId,
                    label: tools
                      .findAncestors(
                        serviceReducer.allServices,
                        orderProduct.serviceId,
                      )
                      ?.filter((e: any, index: number) => index < 3)
                      .reverse()
                      ?.reduce(
                        (acc: string, curr: any, index: number) =>
                          acc + ((index === 0 ? "" : " > ") + curr?.title),
                        "",
                      ),
                  }}
                  onChange={(selected) =>
                    handleServiceItemChange(
                      orderProduct.serviceId,
                      "serviceId",
                      selected.value,
                    )
                  }
                  placeholder="انتخاب خدمت"
                  aria-label="خدمت"
                />
              </div>
              <button
                className="remove-service-btn"
                onClick={() => handleRemoveService(orderProduct.serviceId)}
                aria-label="حذف خدمت"
              >
                <i className="cancelSvg"></i>
              </button>
            </div>

            <div className="service-card-body">
              <div className="quantity-section">
                <label className="service-label">تعداد:</label>
                <div className="quantity-controls">
                  <button
                    className="quantity-btn"
                    onClick={() =>
                      handleServiceItemChange(
                        orderProduct.serviceId,
                        "count",
                        Number(orderProduct.count) + 1,
                      )
                    }
                    aria-label="افزایش تعداد"
                  >
                    +
                  </button>
                  <input
                    type="number"
                    className="quantity-input"
                    value={orderProduct.count}
                    onChange={(e) => {
                      const value = Math.max(1, parseInt(e.target.value) || 1);
                      handleServiceItemChange(
                        orderProduct.serviceId,
                        "count",
                        value,
                      );
                    }}
                    min="1"
                    aria-label="تعداد"
                  />
                  <button
                    className="quantity-btn"
                    onClick={() => {
                      if (orderProduct.count > 1) {
                        handleServiceItemChange(
                          orderProduct.serviceId,
                          "count",
                          Number(orderProduct.count) - 1,
                        );
                      }
                    }}
                    aria-label="کاهش تعداد"
                  >
                    -
                  </button>
                </div>
              </div>

              {isColored && (
                <div className="colors-section">
                  <label className="service-label">رنگ‌ها:</label>
                  <Select
                    styles={getSelectStyles()}
                    options={tools.selectFormatter(
                      colors,
                      "id",
                      "title",
                      "انتخاب کنید",
                    )}
                    value={orderProduct.colors?.map((colorId: number) => ({
                      value: colorId,
                      label: colors.find((color: any) => color.id === colorId)
                        ?.title,
                    }))}
                    className="colors-select"
                    isMulti={true}
                    onChange={(selected: any) =>
                      handleServiceItemChange(
                        orderProduct.serviceId,
                        "colors",
                        selected.map((e: any) => e.value),
                      )
                    }
                    placeholder="انتخاب رنگ"
                    aria-label="رنگ‌ها"
                  />
                </div>
              )}

              <div className="price-section">
                <div className="price-row">
                  <span className="service-label">قیمت واحد:</span>
                  <span className="price-value">
                    {tools.formatPrice(singlePrice)}
                  </span>
                </div>
                <div className="price-row total-price">
                  <span className="service-label">قیمت کل:</span>
                  <span className="price-value">
                    {tools.formatPrice(price)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      } else {
        // Desktop/Tablet table row layout
        return (
          <tr className="service-row" key={`product-${index}`}>
            <td className="backGround1">
              <p>{tools.formatPrice(price)}</p>
            </td>
            <td>
              <p>{tools.formatPrice(singlePrice)}</p>
            </td>
            <td className="quantity">
              <div className="quantityButtom">
                <i
                  className="tablePlusIcon clickable"
                  onClick={() =>
                    handleServiceItemChange(
                      orderProduct.serviceId,
                      "count",
                      Number(orderProduct.count) + 1,
                    )
                  }
                  role="button"
                  aria-label="افزایش تعداد"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    handleServiceItemChange(
                      orderProduct.serviceId,
                      "count",
                      Number(orderProduct.count) + 1,
                    )
                  }
                ></i>
                <input
                  type="number"
                  className="quantityNumber"
                  value={orderProduct.count}
                  onChange={(e) => {
                    const value = Math.max(1, parseInt(e.target.value) || 1);
                    handleServiceItemChange(
                      orderProduct.serviceId,
                      "count",
                      value,
                    );
                  }}
                  min="1"
                  aria-label="تعداد"
                />
                <i
                  className="tableCollapsIcon clickable"
                  onClick={() => {
                    if (orderProduct.count > 1) {
                      handleServiceItemChange(
                        orderProduct.serviceId,
                        "count",
                        Number(orderProduct.count) - 1,
                      );
                    }
                  }}
                  role="button"
                  aria-label="کاهش تعداد"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    orderProduct.count > 1 &&
                    handleServiceItemChange(
                      orderProduct.serviceId,
                      "count",
                      Number(orderProduct.count) - 1,
                    )
                  }
                ></i>
              </div>
            </td>

            {isColored && (
              <td>
                <Select
                  styles={getSelectStyles()}
                  options={tools.selectFormatter(
                    colors,
                    "id",
                    "title",
                    "انتخاب کنید",
                  )}
                  value={orderProduct.colors?.map((colorId: number) => ({
                    value: colorId,
                    label: colors.find((color: any) => color.id === colorId)
                      ?.title,
                  }))}
                  className="orderColorSelect"
                  isMulti={true}
                  onChange={(selected: any) =>
                    handleServiceItemChange(
                      orderProduct.serviceId,
                      "colors",
                      selected.map((e: any) => e.value),
                    )
                  }
                  placeholder="انتخاب رنگ"
                  aria-label="رنگ‌ها"
                />
              </td>
            )}

            <td>
              <Select
                className="orderServiceSelect"
                styles={getSelectStyles()}
                options={serviceReducer.allServices
                  .filter((e: any) => e.price > 0)
                  .map((e: any) => ({
                    value: e.id,
                    label: tools
                      .findAncestors(serviceReducer.allServices, e.id)
                      ?.filter((e: any, index: number) => index < 3)
                      .reverse()
                      ?.reduce(
                        (acc: string, curr: any, index: number) =>
                          acc + ((index === 0 ? "" : " > ") + curr?.title),
                        "",
                      ),
                  }))}
                value={{
                  value: orderProduct.serviceId,
                  label: tools
                    .findAncestors(
                      serviceReducer.allServices,
                      orderProduct.serviceId,
                    )
                    ?.filter((e: any, index: number) => index < 3)
                    .reverse()
                    ?.reduce(
                      (acc: string, curr: any, index: number) =>
                        acc + ((index === 0 ? "" : " > ") + curr?.title),
                      "",
                    ),
                }}
                onChange={(selected) =>
                  handleServiceItemChange(
                    orderProduct.serviceId,
                    "serviceId",
                    selected.value,
                  )
                }
                placeholder="انتخاب خدمت"
                aria-label="خدمت"
              />
            </td>

            <td>
              <i
                className="cancelSvg clickable"
                onClick={() => handleRemoveService(orderProduct.serviceId)}
                role="button"
                aria-label="حذف خدمت"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  handleRemoveService(orderProduct.serviceId)
                }
              ></i>
            </td>
          </tr>
        );
      }
    });
  };

  return (
    <section className="bottom width100 services-section">
      <h6 className="dashBoardTitle">خدمات</h6>

      <div className={`service-selector ${isMobile ? "mobile-selector" : ""}`}>
        <Select
          options={[
            { value: undefined, label: "انتخاب کنید" },
            ...serviceReducer.services.filter((e: any) => !e.parent),
          ].map((e: any) => ({
            value: e.id,
            label:
              serviceReducer.services.find((j: any) => j.id === e?.id)?.title ||
              "انتخاب کنید",
          }))}
          value={{
            value: form?.serviceId,
            label:
              serviceReducer.services.find((e: any) => e.id === form?.serviceId)
                ?.title || "انتخاب کنید",
          }}
          onChange={handleServiceChange}
          styles={getSelectStyles()}
          className="service-category-select"
          placeholder="انتخاب دسته خدمت"
          isSearchable={false}
          aria-label="انتخاب دسته خدمت"
        />

        <button
          className={`add-service-btn ${isMobile ? "mobile-add-btn" : ""}`}
          onClick={handleAddService}
          disabled={!form?.serviceId}
          aria-label="اضافه کردن خدمت"
        >
          {isMobile ? "+" : "اضافه کردن خدمت"}
        </button>
      </div>

      {isMobile ? (
        // Mobile card layout
        <div className="services-mobile-container">
          {form?.orderServices?.length > 0 ? (
            renderServiceRows()
          ) : (
            <div className="no-services-message">
              <p>هیچ خدمتی اضافه نشده است</p>
            </div>
          )}
        </div>
      ) : (
        // Desktop/Tablet table layout
        <div className="services-table-container">
          <table className="servicesTable">
            <thead className="editOrderTable">
              <tr>
                <th className="sideBarTitle center">قیمت کل</th>
                <th className="sideBarTitle center">قیمت واحد</th>
                <th className="sideBarTitle center">تعداد</th>
                {isColored && <th className="sideBarTitle center">رنگ</th>}
                <th className="sideBarTitle center">خدمت</th>
                <th className="sideBarTitle center">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {form?.orderServices?.length > 0 ? (
                renderServiceRows()
              ) : (
                <tr>
                  <td colSpan={isColored ? 6 : 5} className="no-services-row">
                    هیچ خدمتی اضافه نشده است
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default ServicesSection;
